import { RepliersListingDetails } from './repliers/RepliersListingDetails';
import { instanceToPlain } from 'class-transformer';
import { RepliersDataCleaner } from '../utils/RepliersDataCleaner';
import { RepliersListingBalcony } from './repliers/RepliersListingBalcony';

const typesForCleaning = {
  numFireplaces: 'boolean',
  handicappedEquipped: 'boolean',
  familyRoom: 'boolean',
  commonElementsIncluded: 'boolean',
  greenPropertyInformationStatement: 'boolean',
  centralVac: 'boolean',
  numRooms: 'number',
  numRoomsPlus: 'number',
  numBathrooms: 'number',
  numBathroomsPlus: 'number',
  numBedrooms: 'number',
  numBedroomsPlus: 'number',
  numGarageSpaces: 'number',
  numParkingSpaces: 'number',
  parkCostMonthly: 'number',
};

export type ListingDetailsObject = {
  airConditioning: null | string;
  basement1: null | string;
  basement2: null | string;
  balcony: null | RepliersListingBalcony;
  centralVac: null | boolean;
  den: null | string;
  description: null | string;
  elevator: null | string;
  exteriorConstruction1: null | string;
  exteriorConstruction2: null | string;
  extras: null | string;
  furnished: null | string;
  garage: null | string;
  heating: null | string;
  numBathrooms: null | number;
  numBathroomsPlus: null | number;
  numBedrooms: null | number;
  numBedroomsPlus: null | number;
  numFireplaces: null | boolean;
  numGarageSpaces: null | number;
  numParkingSpaces: null | number;
  numRooms: null | number;
  numRoomsPlus: null | number;
  sqft: null | string;
  swimmingPool: null | string;
  virtualTourUrl: null | string;
  yearBuilt: null | string;
  landAccessType: null | string;
  landSewer: null | string;
  viewType: null | string;
  zoningDescription: null | string;
  analyticsClick: null | string;
  analyticsView?: null | string;
  moreInformationLink: null | string;
  alternateURLVideoLink: null | string;
  flooringType: null | string;
  foundationType: null | string;
  landscapeFeatures: null | string;
  fireProtection: null | string;
  roofMaterial: null | string;
  farmType: null | string;
  zoningType: null | string;
  businessType: null | string;
  businessSubType: null | string;
  landDisposition: null | string;
  storageType: null | string;
  constructionStyleSplitLevel: null | string;
  constructionStatus: null | string;
  loadingType: null | string;
  ceilingType: null | string;
  liveStreamEventURL: null | string;
  energuideRating: null | string;
  amperage: null | string;
  waterSource: null | string;
  laundryLevel: null | string;
  certificationLevel: null | string;
  zoning: null | string;
  handicappedEquipped: null | boolean;
  energyCertification: null | string;
  greenPropertyInformationStatement: null | boolean;
  leaseTerms: null | string;
  parkCostMonthly: null | number;
  familyRoom: null | boolean;
  centralAirConditioning: null | string;
  sewer: null | string;
  commonElementsIncluded: null | boolean;
  driveway: null | string;
};

export class ListingDetails {
  airConditioning: null | string;
  basement1: null | string;
  basement2: null | string;
  balcony: null | RepliersListingBalcony;
  centralVac: null | boolean; // NOTE: the value for TREB listings is always Y or N or blank
  den: null | string;
  description: null | string;
  elevator: null | string;
  exteriorConstruction1: null | string;
  exteriorConstruction2: null | string;
  extras: null | string;
  furnished: null | string;
  garage: null | string;
  heating: null | string;
  numBathrooms: null | number;
  numBathroomsPlus: null | number;
  numBedrooms: null | number;
  numBedroomsPlus: null | number;
  numFireplaces: null | boolean; // TO DO: rename (NOTE: the value for TREB listings is always Y or N or blank)
  numGarageSpaces: null | number;
  numParkingSpaces: null | number;
  numRooms: null | number;
  numRoomsPlus: null | number;
  sqft: null | string;
  swimmingPool: null | string;
  virtualTourUrl: null | string;
  yearBuilt: null | string;
  landAccessType: null | string;
  landSewer: null | string;
  viewType: null | string;
  zoningDescription: null | string;
  analyticsClick: null | string;
  analyticsView?: null | string;
  moreInformationLink: null | string;
  alternateURLVideoLink: null | string;
  flooringType: null | string;
  foundationType: null | string;
  landscapeFeatures: null | string;
  fireProtection: null | string;
  roofMaterial: null | string;
  farmType: null | string;
  zoningType: null | string;
  businessType: null | string;
  businessSubType: null | string;
  landDisposition: null | string;
  storageType: null | string;
  constructionStyleSplitLevel: null | string;
  constructionStatus: null | string;
  loadingType: null | string;
  ceilingType: null | string;
  liveStreamEventURL: null | string;
  energuideRating: null | string;
  amperage: null | string;
  waterSource: null | string;

  // The attributes below are not in the Repliers API docs
  laundryLevel: null | string;
  certificationLevel: null | string;
  zoning: null | string;
  handicappedEquipped: null | boolean; // NOTE: the value for TREB listings is always Y or N or blank
  energyCertification: null | string;
  greenPropertyInformationStatement: null | boolean; // NOTE: the value for TREB listings is always Y or N or blank
  leaseTerms: null | string;
  parkCostMonthly: null | number;
  familyRoom: null | boolean; // NOTE: the value for TREB listings is always Y or N or blank
  centralAirConditioning: null | string;
  sewer: null | string;
  commonElementsIncluded: null | boolean;
  driveway: null | string;

  static fromObject(
    listingDetailsObject: ListingDetailsObject,
  ): ListingDetails {
    return new ListingDetails(listingDetailsObject);
  }

  static fromRepliersListingDetails(
    repliersListingDetails: RepliersListingDetails,
  ): ListingDetails {
    const cleaner = new RepliersDataCleaner<
      RepliersListingDetails,
      ListingDetailsObject
    >(repliersListingDetails, { types: typesForCleaning });
    const listingDetailsObject = cleaner.clean();
    return new ListingDetails(listingDetailsObject);
  }

  constructor(listingDetailsObject: ListingDetailsObject) {
    this.airConditioning = listingDetailsObject.airConditioning;
    this.basement1 = listingDetailsObject.basement1;
    this.basement2 = listingDetailsObject.basement2;
    this.balcony = listingDetailsObject.balcony || null;
    this.centralVac = listingDetailsObject.centralVac;
    this.den = listingDetailsObject.den;
    this.description = listingDetailsObject.description;
    this.elevator = listingDetailsObject.elevator;
    this.exteriorConstruction1 = listingDetailsObject.exteriorConstruction1;
    this.exteriorConstruction2 = listingDetailsObject.exteriorConstruction2;
    this.extras = listingDetailsObject.extras;
    this.furnished = listingDetailsObject.furnished;
    this.garage = listingDetailsObject.garage;
    this.heating = listingDetailsObject.heating;
    this.numBathrooms = listingDetailsObject.numBathrooms;
    this.numBathroomsPlus = listingDetailsObject.numBathroomsPlus;
    this.numBedrooms = listingDetailsObject.numBedrooms;
    this.numBedroomsPlus = listingDetailsObject.numBedroomsPlus;
    this.numFireplaces = listingDetailsObject.numFireplaces;
    this.numGarageSpaces = listingDetailsObject.numGarageSpaces;
    this.numParkingSpaces = listingDetailsObject.numParkingSpaces;
    this.numRooms = listingDetailsObject.numRooms;
    this.numRoomsPlus = listingDetailsObject.numRoomsPlus;
    this.sqft = listingDetailsObject.sqft;
    this.swimmingPool = listingDetailsObject.swimmingPool;
    this.virtualTourUrl = listingDetailsObject.virtualTourUrl;
    this.yearBuilt = listingDetailsObject.yearBuilt;
    this.landAccessType = listingDetailsObject.landAccessType;
    this.landSewer = listingDetailsObject.landSewer;
    this.viewType = listingDetailsObject.viewType;
    this.zoningDescription = listingDetailsObject.zoningDescription;
    this.analyticsClick = listingDetailsObject.analyticsClick;
    this.analyticsView = listingDetailsObject.analyticsView;
    this.moreInformationLink = listingDetailsObject.moreInformationLink;
    this.alternateURLVideoLink = listingDetailsObject.alternateURLVideoLink;
    this.flooringType = listingDetailsObject.flooringType;
    this.foundationType = listingDetailsObject.foundationType;
    this.landscapeFeatures = listingDetailsObject.landscapeFeatures;
    this.fireProtection = listingDetailsObject.fireProtection;
    this.roofMaterial = listingDetailsObject.roofMaterial;
    this.farmType = listingDetailsObject.farmType;
    this.zoningType = listingDetailsObject.zoningType;
    this.businessType = listingDetailsObject.businessType;
    this.businessSubType = listingDetailsObject.businessSubType;
    this.landDisposition = listingDetailsObject.landDisposition;
    this.storageType = listingDetailsObject.storageType;
    this.constructionStyleSplitLevel =
      listingDetailsObject.constructionStyleSplitLevel;
    this.constructionStatus = listingDetailsObject.constructionStatus;
    this.loadingType = listingDetailsObject.loadingType;
    this.ceilingType = listingDetailsObject.ceilingType;
    this.liveStreamEventURL = listingDetailsObject.liveStreamEventURL;
    this.energuideRating = listingDetailsObject.energuideRating;
    this.amperage = listingDetailsObject.amperage;
    this.waterSource = listingDetailsObject.waterSource;
    this.laundryLevel = listingDetailsObject.laundryLevel;
    this.certificationLevel = listingDetailsObject.certificationLevel;
    this.zoning = listingDetailsObject.zoning;
    this.handicappedEquipped = listingDetailsObject.handicappedEquipped;
    this.energyCertification = listingDetailsObject.energyCertification;
    this.greenPropertyInformationStatement =
      listingDetailsObject.greenPropertyInformationStatement;
    this.leaseTerms = listingDetailsObject.leaseTerms;
    this.parkCostMonthly = listingDetailsObject.parkCostMonthly;
    this.familyRoom = listingDetailsObject.familyRoom;
    this.centralAirConditioning = listingDetailsObject.centralAirConditioning;
    this.sewer = listingDetailsObject.sewer;
    this.commonElementsIncluded = listingDetailsObject.commonElementsIncluded;
    this.driveway = listingDetailsObject.driveway;
  }

  toObject(): ListingDetailsObject {
    return instanceToPlain(this) as ListingDetailsObject;
  }
}
